import { logger } from "@/lib/logger";
import { useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteContext } from '@/contexts/SiteContext';
import { useSMOCROAccess } from '@/hooks/useSMOCROAccess';
import { useToast } from '@/hooks/use-toast';

export interface MatchingAlert {
  id: string;
  match_score: number;
  alert_priority: string;
  viewed_at: string | null;
  created_at: string;
  metadata: Record<string, any>;
  trial_site_id?: string | null;
}

export type AlertScope = 'admin' | 'organization' | 'trial_site' | 'none';

interface AlertContext {
  scope: AlertScope;
  scopeId: string | null;
  scopeName: string | null;
  effectiveSiteId: string | null;
  organizationId: string | null;
  isAdmin: boolean;
}

interface UseSiteAwareMatchingAlertsResult {
  alerts: MatchingAlert[];
  context: AlertContext;
  unviewedCount: number;
  loading: boolean;
  error: Error | null;
  markAsViewed: (id: string) => Promise<void>;
  refresh: () => void;
}

interface UseSiteAwareMatchingAlertsOptions {
  /** Override the site ID for filtering - used when admin is viewing a specific site's dashboard */
  overrideSiteId?: string | null;
  /** Override the site name for display */
  overrideSiteName?: string | null;
  /** Whether to enable the query (for lazy-loading). Defaults to true. */
  enabled?: boolean;
}

/**
 * Site-aware matching alerts hook
 * 
 * Filtering logic:
 * - If overrideSiteId is provided: Filter to that specific site (page context takes priority)
 * - ADMIN (no override): See ALL platform matching alerts (no filtering)
 * - CRO/SMO Users: See alerts for their organization's trial sites
 * - Trial Site Staff: See alerts for their specific site only
 * - Other: No matching alerts (personal scope)
 * 
 * This ensures St Luke's site only sees St Luke's alerts, not platform-wide alerts.
 */
export function useSiteAwareMatchingAlerts(options?: UseSiteAwareMatchingAlertsOptions): UseSiteAwareMatchingAlertsResult {
  const { overrideSiteId, overrideSiteName, enabled: externalEnabled = true } = options || {};
  const { user, userProfile, isAdmin } = useAuth();
  const { site } = useSiteContext();
  const { isSMOCROUser, organizationId, organizationName } = useSMOCROAccess();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Determine the effective context for alert filtering
  const alertContext = useMemo((): AlertContext => {
    // Priority 0: Override from page context (admin viewing specific site)
    if (overrideSiteId) {
      logger.debug(`[SiteAwareMatchingAlerts] Using override site ID: ${overrideSiteId}`);
      return {
        scope: 'trial_site',
        scopeId: overrideSiteId,
        scopeName: overrideSiteName || 'Site Dashboard',
        effectiveSiteId: overrideSiteId,
        organizationId: null,
        isAdmin: isAdmin, // Still track if user is admin
      };
    }

    // Priority 1: If on a subdomain site, scope to that site (even for admins)
    if (site?.id) {
      logger.debug(`[SiteAwareMatchingAlerts] Subdomain site context detected: ${site.id}`);
      return {
        scope: 'trial_site',
        scopeId: site.id,
        scopeName: site.display_name || 'Trial Site',
        effectiveSiteId: site.id,
        organizationId: null,
        isAdmin: isAdmin,
      };
    }

    // Priority 2: Admin users on main domain see everything
    if (isAdmin) {
      return {
        scope: 'admin',
        scopeId: null,
        scopeName: 'Platform-wide',
        effectiveSiteId: null,
        organizationId: null,
        isAdmin: true,
      };
    }

    // Priority 2: CRO/SMO users see their organization's alerts
    if (isSMOCROUser && organizationId) {
      return {
        scope: 'organization',
        scopeId: organizationId,
        scopeName: organizationName || 'Organization',
        effectiveSiteId: null,
        organizationId: organizationId,
        isAdmin: false,
      };
    }

    // Priority 4: Site context from user profile
    if (userProfile?.trial_site_id) {
      return {
        scope: 'trial_site',
        scopeId: userProfile.trial_site_id,
        scopeName: 'Your Site',
        effectiveSiteId: userProfile.trial_site_id,
        organizationId: null,
        isAdmin: false,
      };
    }

    // Fallback: No matching alerts for personal scope
    return {
      scope: 'none',
      scopeId: null,
      scopeName: null,
      effectiveSiteId: null,
      organizationId: null,
      isAdmin: false,
    };
  }, [isAdmin, isSMOCROUser, organizationId, organizationName, site, userProfile?.trial_site_id, overrideSiteId, overrideSiteName]);

  // Fetch organization's trial sites for org-level filtering
  const { data: orgTrialSites } = useQuery({
    queryKey: ['org-trial-sites', alertContext.organizationId],
    queryFn: async () => {
      if (!alertContext.organizationId) return [];
      
      const { data, error } = await supabase
        .from('trial_sites_safe')
        .select('id')
        .eq('organization_id', alertContext.organizationId);
      
      if (error) {
        logger.error('[SiteAwareMatchingAlerts] Error fetching org sites:', error);
        return [];
      }
      
      return data?.map(s => s.id) || [];
    },
    enabled: alertContext.scope === 'organization' && !!alertContext.organizationId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Build the scoped query for matching alerts
  const fetchAlerts = useCallback(async (): Promise<MatchingAlert[]> => {
    // If scope is 'none', return empty array - no alerts for personal users
    if (alertContext.scope === 'none') {
      logger.debug('[SiteAwareMatchingAlerts] Personal scope - no matching alerts');
      return [];
    }

    logger.debug(`[SiteAwareMatchingAlerts] Fetching alerts for scope: ${alertContext.scope}, siteId: ${alertContext.effectiveSiteId || 'N/A'}`);

    // First, get alert data - we'll filter in-memory for metadata.trial_site_id
    // until the dedicated column is backfilled
    const { data, error } = await supabase
      .from('trial_matching_alerts')
      .select('id, match_score, alert_priority, viewed_at, created_at, metadata, trial_site_id')
      .is('viewed_at', null)
      .order('created_at', { ascending: false })
      .limit(100); // Fetch more for filtering

    if (error) {
      logger.error('[SiteAwareMatchingAlerts] Fetch error:', error);
      throw error;
    }

    let filteredAlerts = (data || []) as MatchingAlert[];

    // Apply role-based filtering
    switch (alertContext.scope) {
      case 'admin':
        // Admin sees all - no filter, just limit to 20
        filteredAlerts = filteredAlerts.slice(0, 20);
        logger.debug(`[SiteAwareMatchingAlerts] Admin scope - showing ${filteredAlerts.length} alerts`);
        break;

      case 'organization':
        // CRO/SMO sees alerts for their organization's trial sites
        if (orgTrialSites && orgTrialSites.length > 0) {
          filteredAlerts = filteredAlerts.filter(alert => {
            // Check both dedicated column and metadata
            const siteId = alert.trial_site_id || (alert.metadata as any)?.trial_site_id;
            return siteId && orgTrialSites.includes(siteId);
          }).slice(0, 20);
          logger.debug(`[SiteAwareMatchingAlerts] Org scope - ${orgTrialSites.length} sites, showing ${filteredAlerts.length} alerts`);
        } else {
          filteredAlerts = [];
          logger.debug('[SiteAwareMatchingAlerts] Org scope - no trial sites found');
        }
        break;

      case 'trial_site':
        // Trial site staff sees only their site's alerts
        if (alertContext.effectiveSiteId) {
          filteredAlerts = filteredAlerts.filter(alert => {
            // Check both dedicated column and metadata for backward compatibility
            const alertSiteId = alert.trial_site_id || (alert.metadata as any)?.trial_site_id;
            return alertSiteId === alertContext.effectiveSiteId;
          }).slice(0, 20);
          logger.debug(`[SiteAwareMatchingAlerts] Site scope (${alertContext.effectiveSiteId}) - showing ${filteredAlerts.length} alerts`);
        } else {
          filteredAlerts = [];
        }
        break;

      default:
        filteredAlerts = [];
    }

    return filteredAlerts;
  }, [alertContext, orgTrialSites]);

  // Use React Query for data fetching with caching
  const {
    data: alerts = [],
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['site-aware-matching-alerts', alertContext.scope, alertContext.scopeId, orgTrialSites?.length],
    queryFn: fetchAlerts,
    enabled: alertContext.scope !== 'none' && externalEnabled,
    staleTime: 60 * 1000, // 1 minute (was 30s — reduce pressure)
    refetchOnWindowFocus: false, // Hardened: don't refetch on focus
  });

  // Set up real-time subscription based on scope
  useEffect(() => {
    if (alertContext.scope === 'none') return;

    // Build channel name based on scope
    const channelName = alertContext.scope === 'admin'
      ? 'matching_alerts:admin'
      : alertContext.scope === 'organization' && alertContext.organizationId
        ? `matching_alerts:org:${alertContext.organizationId}`
        : alertContext.scope === 'trial_site' && alertContext.effectiveSiteId
          ? `matching_alerts:site:${alertContext.effectiveSiteId}`
          : 'matching_alerts:none';

    logger.debug(`[SiteAwareMatchingAlerts] Setting up real-time channel: ${channelName}`);

    // For site-specific subscriptions, use filter on the trial_site_id column
    const subscriptionConfig: any = {
      event: 'INSERT',
      schema: 'public',
      table: 'trial_matching_alerts',
    };

    // Add filter for site-specific subscriptions when column exists
    if (alertContext.scope === 'trial_site' && alertContext.effectiveSiteId) {
      subscriptionConfig.filter = `trial_site_id=eq.${alertContext.effectiveSiteId}`;
    }

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', subscriptionConfig, (payload) => {
        logger.debug('[SiteAwareMatchingAlerts] New alert received:', payload);
        
        // For org scope, we need to verify the alert belongs to our sites
        if (alertContext.scope === 'organization' && orgTrialSites) {
          const newAlert = payload.new as MatchingAlert;
          const alertSiteId = newAlert.trial_site_id || (newAlert.metadata as any)?.trial_site_id;
          if (!alertSiteId || !orgTrialSites.includes(alertSiteId)) {
            logger.debug('[SiteAwareMatchingAlerts] Alert not for our org sites, ignoring');
            return;
          }
        }

        // For site scope without column filter (fallback for metadata check)
        if (alertContext.scope === 'trial_site' && alertContext.effectiveSiteId && !subscriptionConfig.filter) {
          const newAlert = payload.new as MatchingAlert;
          const alertSiteId = newAlert.trial_site_id || (newAlert.metadata as any)?.trial_site_id;
          if (alertSiteId !== alertContext.effectiveSiteId) {
            logger.debug('[SiteAwareMatchingAlerts] Alert not for our site, ignoring');
            return;
          }
        }

        // Invalidate query to refetch
        queryClient.invalidateQueries({ 
          queryKey: ['site-aware-matching-alerts'] 
        });

        // Show toast for critical alerts
        const newAlert = payload.new as MatchingAlert;
        if (newAlert.alert_priority === 'critical') {
          toast({
            title: `🚨 Critical Match Alert (${newAlert.match_score}%)`,
            description: (newAlert.metadata as any)?.trial_title || 'High-quality patient match',
            duration: 8000,
          });
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [alertContext, orgTrialSites, queryClient, toast]);

  // Mark alert as viewed
  const markAsViewed = useCallback(async (alertId: string) => {
    try {
      const { error } = await supabase
        .from('trial_matching_alerts')
        .update({ viewed_at: new Date().toISOString() })
        .eq('id', alertId);

      if (error) throw error;

      // Optimistically update local cache
      queryClient.setQueryData<MatchingAlert[]>(
        ['site-aware-matching-alerts', alertContext.scope, alertContext.scopeId, orgTrialSites?.length],
        (old) => old?.filter(a => a.id !== alertId) || []
      );
    } catch (error) {
      logger.error('[SiteAwareMatchingAlerts] Error marking as viewed:', error);
    }
  }, [queryClient, alertContext, orgTrialSites?.length]);

  // Calculate unviewed count
  const unviewedCount = useMemo(() => alerts.length, [alerts]);

  return {
    alerts,
    context: alertContext,
    unviewedCount,
    loading,
    error: error as Error | null,
    markAsViewed,
    refresh: refetch,
  };
}
